import React, { useCallback } from "react";
import classNames from "classnames";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { Amount } from "@design-system/Amount/Amount";
import { ClickableContainer } from "@design-system/ClickableContainer/ClickableContainer";
import { Entity } from "@common/types/entity";
import { formatBusinessDate, formatDateOnly } from "@helpers/date";
import { DetachIcon, SearchIcon } from "@icons";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import styles from "./MatchedTransactionCard.module.scss";
import shellStyles from "./MatchCardShell.module.scss";

interface MatchedTransactionCardPairedTransaction {
    date: Date | string;
    dateOnly?: string | null;
    amount: number;
    bankDescription: string;
    counterparty?: { name: string } | null;
    entity: Pick<Entity, "id" | "name" | "iconColor" | "workspaceId" | "logo">;
}

export function formatMatchedTransactionDate(
    pairedTransaction: Pick<
        MatchedTransactionCardPairedTransaction,
        "date" | "dateOnly"
    >,
): string {
    return pairedTransaction.dateOnly
        ? formatDateOnly(pairedTransaction.dateOnly)
        : formatBusinessDate(new Date(pairedTransaction.date));
}

function getRowLabel(
    pairedTransaction: MatchedTransactionCardPairedTransaction,
): string {
    return (
        pairedTransaction.counterparty?.name ??
        pairedTransaction.bankDescription
    );
}

interface Props {
    label: string;
    pairedTransaction: MatchedTransactionCardPairedTransaction;
    onClick: () => void;
    onDetach: () => void | Promise<void>;
    detachTooltip?: string;
    replaceTooltip?: string;
    className?: string;
}

export const MatchedTransactionCard: React.FC<Props> = ({
    label,
    pairedTransaction,
    onClick,
    onDetach,
    detachTooltip = "Unmatch transaction",
    replaceTooltip = "Replace transaction match",
    className,
}) => {
    const bankDescription = pairedTransaction.bankDescription;
    const rowLabel = getRowLabel(pairedTransaction);

    const handleDetach = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            void onDetach();
        },
        [onDetach],
    );

    return (
        <div className={className}>
            <HStack
                as={ClickableContainer}
                onClick={onClick}
                justify="between"
                align="center"
                className={classNames(
                    "mb-0 cursor-pointer",
                    shellStyles.control,
                    styles.bottomBorderNoRadius,
                )}
            >
                <span className={shellStyles.label}>{label}</span>

                <div className={shellStyles.iconsContainer}>
                    <Tooltip content={detachTooltip}>
                        <button
                            className={shellStyles.iconButton}
                            onClick={handleDetach}
                            aria-label={detachTooltip}
                            type="button"
                        >
                            <DetachIcon />
                        </button>
                    </Tooltip>
                    <Tooltip content={replaceTooltip}>
                        <div
                            className={shellStyles.iconButton}
                            aria-label={replaceTooltip}
                        >
                            <SearchIcon />
                        </div>
                    </Tooltip>
                </div>
            </HStack>

            <Tooltip
                content={
                    <VStack gap={4}>
                        <HStack gap={4} align="center">
                            <EntityAvatar
                                size="xs"
                                entity={pairedTransaction.entity}
                                aria-hidden
                            />
                            <span className="text-gray-700 font-weight-bold">
                                {pairedTransaction.entity.name}
                            </span>
                        </HStack>
                        {bankDescription}
                    </VStack>
                }
            >
                <div className={styles.cardFooter}>
                    <div className={styles.cardFooterText}>
                        <span>
                            {formatMatchedTransactionDate(pairedTransaction)}
                        </span>
                        <span className={styles.truncatedText}>{rowLabel}</span>
                    </div>
                    <div className={styles.cardFooterText}>
                        <span
                            className={classNames(
                                styles.cardFooterTextAmount,
                                pairedTransaction.amount > 0 &&
                                    styles.cardFooterTextAmountPositive,
                            )}
                        >
                            <Amount
                                amount={pairedTransaction.amount}
                                separateCurrencySign
                                showNegative
                            />
                        </span>
                    </div>
                </div>
            </Tooltip>
        </div>
    );
};

export interface MatchedTransactionRowData extends MatchedTransactionCardPairedTransaction {
    key: string;
    onClick: () => void;
    onDetach: () => void | Promise<void>;
    detachTooltip?: string;
}

interface MatchedTransactionsCardProps {
    label: string;
    rows: MatchedTransactionRowData[];
    className?: string;
    onHeaderClick?: () => void;
    headerIcon?: React.ReactNode;
}

export const MatchedTransactionsCard: React.FC<
    MatchedTransactionsCardProps
> = ({ label, rows, className, onHeaderClick, headerIcon }) => {
    if (rows.length === 0) {
        return null;
    }

    const headerContent = (
        <>
            <span className={shellStyles.insideLabel}>{label}</span>
            {headerIcon}
        </>
    );

    return (
        <div className={classNames(shellStyles.shell, className)}>
            {onHeaderClick ? (
                <HStack
                    as={ClickableContainer}
                    onClick={onHeaderClick}
                    justify="between"
                    align="center"
                    className={classNames(
                        "mb-0 cursor-pointer",
                        shellStyles.control,
                        styles.bottomBorderNoRadius,
                    )}
                >
                    {headerContent}
                </HStack>
            ) : (
                <HStack
                    justify="between"
                    align="center"
                    className={classNames(
                        "mb-0",
                        shellStyles.control,
                        styles.bottomBorderNoRadius,
                    )}
                >
                    {headerContent}
                </HStack>
            )}

            <div className={styles.rowsBody}>
                {rows.map((row) => (
                    <MatchedTransactionsCardRow key={row.key} row={row} />
                ))}
            </div>
        </div>
    );
};

const MatchedTransactionsCardRow: React.FC<{
    row: MatchedTransactionRowData;
}> = ({ row }) => {
    const rowLabel = getRowLabel(row);
    const detachTooltip = row.detachTooltip ?? "Unmatch transaction";
    const { onDetach } = row;

    const handleDetach = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            void onDetach();
        },
        [onDetach],
    );

    return (
        <ClickableContainer
            className={styles.rowsBodyRow}
            onClick={row.onClick}
        >
            <Tooltip
                content={
                    <VStack gap={4}>
                        <HStack gap={4} align="center">
                            <EntityAvatar
                                size="xs"
                                entity={row.entity}
                                aria-hidden
                            />
                            <span className="text-gray-700 font-weight-bold">
                                {row.entity.name}
                            </span>
                        </HStack>
                        {row.bankDescription}
                    </VStack>
                }
            >
                <div className={styles.rowsBodyRowLeft}>
                    <span className={styles.cardFooterText}>
                        {formatMatchedTransactionDate(row)}
                    </span>
                    <span
                        className={classNames(
                            styles.cardFooterText,
                            styles.truncatedText,
                        )}
                    >
                        {rowLabel}
                    </span>
                </div>
            </Tooltip>
            <div className={styles.rowsBodyRowRight}>
                <span
                    className={classNames(
                        styles.cardFooterTextAmount,
                        row.amount > 0 && styles.cardFooterTextAmountPositive,
                    )}
                >
                    <Amount
                        amount={row.amount}
                        separateCurrencySign
                        showNegative
                    />
                </span>
                <Tooltip content={detachTooltip}>
                    <button
                        type="button"
                        className={styles.rowDetachButton}
                        onClick={handleDetach}
                        aria-label={detachTooltip}
                    >
                        <DetachIcon />
                    </button>
                </Tooltip>
            </div>
        </ClickableContainer>
    );
};
